import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ProductRepository } from '@asm/features/sync/repositories/product.repository';
import { B2BUnitHierarchyRepository } from '@asm/features/sync/repositories/b2bunit-hierarchy.repository';
import { Cart, CartEntry } from '@asm/features/quick-order/models/cart.model';
import { ManualCondition } from '@asm/features/sync/services/pricing/pricing.interfaces';
import {
  PromotionRepository,
  LocalPromotion,
  LocalPromotionCondition,
  LocalPromotionAction,
} from '@asm/features/sync/repositories/promotion.repository';

/** Par {model, code} usado para avaliação de condições MODEL. */
interface DynamicFilter {
  model: string;
  code: string;
}

/**
 * Motor de promoções offline.
 *
 * Replica a lógica do `DefaultPromotionEngineService` + script `PROMOTION_CONTEXT`
 * do backend, operando 100% sobre dados do IndexedDB.
 *
 * ## Fluxo
 * 1. Carrega produto e unidade B2B do IndexedDB.
 * 2. Constrói `dynamicFilters` (substitui o PROMOTION_CONTEXT script online):
 *    produto + categorias + campos da unidade B2B + hierarquia de unidades pai + cupom ativo.
 * 3. Carrega promoções ativas (filtradas por data).
 * 4. Para cada promoção, verifica elegibilidade (cupom, excludedPromotions, condições AND/OR).
 * 5. Seleciona a promoção de maior prioridade por `sapConditionType`.
 * 6. Converte as ações qualificadas em `ManualCondition[]` para o motor de precificação.
 *
 * ## Diferença chave em relação ao online
 * - Online: `PromotionConditionValueRepository.hasMatchingValues()` → query no banco.
 * - Offline: `condition.values.some(v => dynamicFilters.some(f => f.model===v.model && f.code===v.code))`.
 *   Os valores já estão embutidos na promoção (desnormalizados no payload do apiCode `Promotions`).
 */
@Injectable({ providedIn: 'root' })
export class DefaultLocalPromotionEngineService {
  private readonly promotionRepo = inject(PromotionRepository);
  private readonly productRepo = inject(ProductRepository);
  private readonly b2bUnitDetailsRepo = inject(B2BUnitHierarchyRepository);

  async getPromotionConditions(
    productCode: string,
    b2bUnitId: string,
    cart: Cart,
    entry: CartEntry,
    t685Records: any[],
  ): Promise<ManualCondition[]> {
    try {
      const [product, b2bUnits, promotions] = await Promise.all([
        firstValueFrom(this.productRepo.getByKey(productCode)),
        firstValueFrom(this.b2bUnitDetailsRepo.getAll()),
        firstValueFrom(this.promotionRepo.getActivePromotions()),
      ]);

      // Filter by the b2bUnitId parameter so the correct unit is used
      // when multiple B2BUnits are present in the store.
      const b2bUnit = b2bUnits.find(u => u.uid === b2bUnitId) ?? b2bUnits[0] ?? null;
      const dynamicFilters = this.buildDynamicFilters(product as any, b2bUnit, cart);

      if (promotions.length === 0) return [];

      const qualified: Array<{ promotion: LocalPromotion; action: LocalPromotionAction; priority: number }> = [];

      for (const promotion of promotions) {
        // Promoções de cupom: avaliar apenas se o cupom ativo bate
        if (!promotion.applyAutomatically && promotion.code !== cart.couponCode) {
          continue;
        }

        // Respeitar exclusões manuais por entry
        if (entry.excludedPromotions?.includes(promotion.sapConditionType)) {
          continue;
        }

        const conditionsMet = this.evaluateConditions(promotion, cart, dynamicFilters);
        if (!conditionsMet) continue;

        const action = this.findFirstApplicableAction(promotion, dynamicFilters);
        if (!action) continue;

        qualified.push({ promotion, action, priority: promotion.priority });
      }

      const best = this.getHighestPriorityByConditionType(qualified);
      return best.map(item => this.toManualCondition(item.promotion, item.action, t685Records));
    } catch {
      return [];
    }
  }

  // ─── Construção dos dynamic filters ────────────────────────────────────────

  /**
   * Constrói os dynamic filters a partir dos dados locais.
   *
   * Substitui o script `PROMOTION_CONTEXT` do backend:
   * - Produto + todas as categorias (diretas + ancestrais, conforme sincronizadas)
   * - Unidade B2B + campos de segmentação + UIDs das unidades pai
   * - Cupom ativo no carrinho
   */
  private buildDynamicFilters(product: any, b2bUnit: any, cart: Cart): DynamicFilter[] {
    const filters: DynamicFilter[] = [];

    // Produto
    if (product?.code) {
      filters.push({ model: 'ProductModel', code: product.code });
    }

    // Categorias do produto (diretas + ancestrais via ProductCategoryLinks)
    for (const cat of product?.categories ?? []) {
      if (cat?.code) {
        filters.push({ model: 'CategoryModel', code: cat.code });
      }
    }

    // Unidade B2B
    if (b2bUnit?.uid) {
      filters.push({ model: 'B2BUnitModel', code: b2bUnit.uid });
    }
    if (b2bUnit?.salesRegion) {
      filters.push({ model: 'SalesRegionModel', code: b2bUnit.salesRegion });
    }
    if (b2bUnit?.cluster) {
      filters.push({ model: 'ClusterModel', code: b2bUnit.cluster });
    }
    if (b2bUnit?.distributionChannel) {
      filters.push({ model: 'DistributionChannelModel', code: b2bUnit.distributionChannel });
    }
    if (b2bUnit?.salesOrganization) {
      filters.push({ model: 'SalesOrganizationModel', code: b2bUnit.salesOrganization });
    }
    // Hierarquia de unidades pai (via getSuperUnits() no backend — FASE 1.5)
    for (const parentUid of b2bUnit?.parentUnitUids ?? []) {
      if (parentUid) {
        filters.push({ model: 'B2BUnitModel', code: parentUid });
      }
    }

    // Cupom ativo
    if (cart.couponCode) {
      filters.push({ model: 'CouponModel', code: cart.couponCode });
    }

    return filters;
  }

  // ─── Avaliação de condições ─────────────────────────────────────────────────

  /**
   * Avalia todas as condições de uma promoção respeitando `conditionGroupOperator`.
   * Implementa short-circuit: OR para no primeiro `true`, AND para no primeiro `false`.
   */
  private evaluateConditions(promotion: LocalPromotion, cart: Cart, dynamicFilters: DynamicFilter[]): boolean {
    const conditions = promotion.conditions ?? [];
    if (conditions.length === 0) return true;

    const isOr = promotion.conditionGroupOperator === 'OR';

    for (const condition of conditions) {
      const passed = this.evaluateSingleCondition(condition, cart, dynamicFilters);
      if (isOr && passed) return true;
      if (!isOr && !passed) return false;
    }

    return !isOr; // OR: nenhuma passou → false | AND: todas passaram → true
  }

  /**
   * Avalia uma condição individual.
   *
   * - Scalar (`value != null`): compara `cart.totalPrice` com o operador configurado.
   * - MODEL: verifica se algum valor da condição bate com os dynamicFilters.
   *          Substitui `PromotionConditionValueRepository.hasMatchingValues()` online.
   */
  private evaluateSingleCondition(
    condition: LocalPromotionCondition,
    cart: Cart,
    dynamicFilters: DynamicFilter[],
  ): boolean {
    if (condition.value != null) {
      // Condição scalar (ex: ORDER_TOTAL_VALUE)
      const contextValue = cart.totalPrice ?? 0;
      return this.compareValues(contextValue, condition.value, condition.operatorCode);
    }

    // Condição MODEL: sem valores = sem restrição
    if (!condition.values || condition.values.length === 0) return true;

    return condition.values.some(v => dynamicFilters.some(f => f.model === v.model && f.code === v.code));
  }

  private compareValues(contextValue: number, targetValue: number, operatorCode?: string): boolean {
    switch (operatorCode) {
      case 'EQUALS':
        return contextValue === targetValue;
      case 'GREATER_THAN':
        return contextValue > targetValue;
      case 'GREATER_THAN_OR_EQUAL':
      case 'GREATER_THAN_OR_EQUALS':
        return contextValue >= targetValue;
      case 'LESS_THAN':
        return contextValue < targetValue;
      case 'LESS_THAN_OR_EQUAL':
        return contextValue <= targetValue;
      default:
        return contextValue >= targetValue;
    }
  }

  // ─── Seleção de ação ────────────────────────────────────────────────────────

  /**
   * Retorna a primeira ação aplicável da promoção.
   * Para ações MODEL, verifica se algum actionValue bate com os dynamicFilters.
   */
  private findFirstApplicableAction(
    promotion: LocalPromotion,
    dynamicFilters: DynamicFilter[],
  ): LocalPromotionAction | null {
    const actions = promotion.actions ?? [];

    for (const action of actions) {
      // Ação numérica direta — sempre aplicável
      if (action.discountValue != null) return action;

      // Ação MODEL — verifica se algum actionValue bate com os filters
      if (action.values && action.values.length > 0) {
        const matches = action.values.some(v => dynamicFilters.some(f => f.model === v.model && f.code === v.code));
        if (matches) return action;
      }
    }

    return null;
  }

  // ─── Prioridade por conditionType ──────────────────────────────────────────

  /**
   * Seleciona a promoção de maior prioridade por `sapConditionType`.
   * Espelha `getHighestPriorityByCondition()` do `DefaultPromotionEngineService`.
   */
  private getHighestPriorityByConditionType(
    entries: Array<{ promotion: LocalPromotion; action: LocalPromotionAction; priority: number }>,
  ): Array<{ promotion: LocalPromotion; action: LocalPromotionAction }> {
    const best = new Map<string, { promotion: LocalPromotion; action: LocalPromotionAction; priority: number }>();

    for (const entry of entries) {
      const condType = entry.promotion.sapConditionType;
      const current = best.get(condType);
      if (!current || entry.priority > current.priority) {
        best.set(condType, entry);
      }
    }

    return Array.from(best.values());
  }

  // ─── Conversão para ManualCondition ────────────────────────────────────────

  /**
   * Converte uma promoção qualificada em `ManualCondition`.
   * Espelha `convertPromotionToManualConditions()` do backend.
   *
   * O `calculationLevel` é lido do T685 pelo `sapConditionType` para garantir
   * a posição correta no pipeline de cálculo de preço.
   */
  private toManualCondition(
    promotion: LocalPromotion,
    action: LocalPromotionAction,
    t685Records: any[],
  ): ManualCondition {
    const t685 = t685Records.find(t => t.KSCHL === promotion.sapConditionType);
    const calculationLevel: number | undefined = t685?.calculationLevel;

    const conditionRate = Number(action.discountValue ?? action.values?.[0]?.kValue ?? 0);
    const conditionUnit = action.calculationType === 'A' ? '%' : 'BRL';

    return {
      condition: promotion.sapConditionType,
      description: promotion.message ?? `Promocao ${promotion.code}`,
      conditionRate,
      conditionUnit,
      calculationType: action.calculationType,
      conditionClass: action.conditionClass,
      calculationLevel,
      baseConditionKey: action.baseConditionKey,
    };
  }
}
